import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import {
  selectLatestStatus,
  selectLatestSubjectCount,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useGetCohortSummaryByCohortProfileIdQuery } from '@verantos-dev/data-access-cohort-summary';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useCohortCharacteristicsTabularDataDialog } from './useCohortCharacteristicsTabularDataDialog';

export const useCohortCharacteristics = ({
  projectId,
  cohortId,
}: {
  projectId: string;
  cohortId: string;
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { displayDataDialog, onHideDataDialog, onShowDataDialog } =
    useCohortCharacteristicsTabularDataDialog();

  const latestCohortStatus = useCohortDefinitionStore(selectLatestStatus);
  const latestSubjectCount = useCohortDefinitionStore(selectLatestSubjectCount);

  const {
    isLoading,
    isUninitialized,
    error,
    data: cohortSummaryData,
    refetch,
  } = useGetCohortSummaryByCohortProfileIdQuery(
    { projectId, cohortProfileId: cohortId },
    {
      skip: !projectId || !cohortId || latestCohortStatus !== CohortDefinitionStatus.PROCESSED,
    }
  );

  useEffect(() => {
    if (latestCohortStatus === CohortDefinitionStatus.PROCESSED) {
      setTimeout(() => {
        refetch();
      }, 1000);
    }
  }, [latestCohortStatus]);

  const totalSubjectCount = latestSubjectCount || 0;

  const cohortMessages = {
    title: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.title',
    }),
    processingTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.processing.title',
    }),
    processingMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.processing.message',
    }),
    draftTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.draft.title',
    }),
    draftMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.draft.message',
    }),
    errorTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.error.title',
    }),
    errorMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.error.message',
    }),
    loadingTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.loading.title',
    }),
    loadingCustomSelectionTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.loading.customSelection.title',
    }),
  };

  const hasSummaryData = !!(
    latestSubjectCount && // if the cohort has subjects
    latestSubjectCount > 0 && // if the cohort has subjects
    cohortSummaryData && // if the cohort has summary data
    (cohortSummaryData?.data?.ageAtIndex?.length > 0 ||
      cohortSummaryData?.data?.ethnicity?.length > 0 ||
      cohortSummaryData?.data?.gender?.length > 0 ||
      cohortSummaryData?.data?.race?.length > 0 ||
      cohortSummaryData?.data?.ccCondition?.length > 0 ||
      cohortSummaryData?.data?.ccDrug?.length > 0 ||
      cohortSummaryData?.data?.ccMeasurement?.length > 0 ||
      cohortSummaryData?.data?.ccObservation?.length > 0 ||
      cohortSummaryData?.data?.ccProcedure?.length > 0 ||
      cohortSummaryData?.data?.ccVisit?.length > 0)
  );

  return {
    theme,
    latestCohortStatus,
    characteristicsError: error,
    isCohortSummaryInitiallyLoading: isLoading || isUninitialized,

    displayDataDialog,
    onHideDataDialog,
    onShowDataDialog,
    totalSubjectCount,
    cohortSummaryData: cohortSummaryData?.data,
    hasSummaryData,
    cohortMessages,
  };
};
